import DatePicker, { ranges } from '@/components/DatePicker';
import TrakowAssistantButton from '@/components/TrakowAssistantButton';
import TrakowAssistantDrawer from '@/components/TrakowAssistantDrawer';
import { ShipmentAnalyticsTabs } from '@/constants';
import { AccountPlan } from '@/constants/enum';
import { getSomeDaysAgo, isLockFeature } from '@/helpers';
import { useGetAiInsightsMutation } from '@/redux/api/api.caller';
import commonSlice, { shouldOpenTrakowAssistantSelector } from '@/redux/features/common.slice';
import { IAiInsightsData } from '@/types/analytics';
import { Icon, InlineStack, LegacyTabs, Page, Text, Tooltip } from '@shopify/polaris';
import { InfoIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FulfillmentPerformance from './FulfillmentPerformance';
import ShipmentsOverview from './ShipmentsOverview';
import { TabBadge } from './components/TabBadge';
import { StyledShipmentAnalytics } from './styled';

interface ITabConfig {
  id: string;
  content: React.ReactNode;
  panelID: string;
  component: React.ReactNode;
}

const ShipmentAnalytics = () => {
  const dispatch = useDispatch();
  const shouldOpenTrakowAssistant = useSelector(shouldOpenTrakowAssistantSelector);
  const [tabSelected, setTabSelected] = useState<string>(ShipmentAnalyticsTabs.ShipmentsOverview);
  const [start, setStart] = useState<Date>(getSomeDaysAgo(30));
  const [end, setEnd] = useState<Date>(new Date());
  const [titleDatePicker, setTitleDatePicker] = useState<string>('Last 30 days');
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [aiInsightsData, setAiInsightsData] = useState<IAiInsightsData | null>(null);
  const [aiInsightsCache, setAiInsightsCache] = useState<{
    data: IAiInsightsData;
    cacheKey: string;
    timestamp: number;
  } | null>(null);
  const [getAiInsights, { isLoading: isLoadingInsights }] = useGetAiInsightsMutation();
  // Cache duration: 1 hour in milliseconds
  const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

  const isViewDemo = useMemo(() => {
    if (tabSelected === ShipmentAnalyticsTabs.FulfillmentPerformance) {
      return isLockFeature([AccountPlan.Starter, AccountPlan.Basic]);
    }
    return false;
  }, [tabSelected]);

  const tabIndex = Object.values(ShipmentAnalyticsTabs).findIndex((tab) => tab === tabSelected);

  const handleChangeTab = (selectedTabIndex: number) => {
    setTabSelected(Object.values(ShipmentAnalyticsTabs)[selectedTabIndex]);
  };

  const handleChangeDatePicker = (newStart: Date, newEnd: Date) => {
    setStart(newStart);
    setEnd(newEnd);
  };

  const handleChangeTitleBtn = (titleBtn: string) => {
    setTitleDatePicker(titleBtn);
  };

  const handleOpenDrawer = async () => {
    // Determine the type based on the active tab
    const insightsType = tabSelected === ShipmentAnalyticsTabs.FulfillmentPerformance ? 'fulfillment' : 'shipments';

    // Create cache key based on titleDatePicker and tab (date range + tab identifier)
    const cacheKey = `${titleDatePicker}-${insightsType}`;
    const now = Date.now();

    // Check if we have valid cached data for this date range and tab
    const isCacheValid =
      aiInsightsCache && aiInsightsCache.cacheKey === cacheKey && now - aiInsightsCache.timestamp < CACHE_DURATION_MS;

    if (isCacheValid) {
      // Use cached data - instant open with no API call!
      setAiInsightsData(aiInsightsCache.data);
      setIsDrawerOpen(true);
      return;
    }

    // Find the alias (backend enum value) for the current titleDatePicker
    const dateTypeAlias = ranges.find((range) => range.title === titleDatePicker)?.alias;

    // No valid cache - fetch fresh data from API
    try {
      const result = await getAiInsights({
        fromDate: start.toISOString(),
        toDate: end.toISOString(),
        type: insightsType, // Pass 'shipments' or 'fulfillment' based on active tab
        mode: 'depthin', // Use 'depthin' mode for detailed analysis
        dateType: dateTypeAlias, // Use the alias (e.g., 'thismonth' instead of 'This month')
      }).unwrap();

      if (result.state === 1 && result.data) {
        // Store in both current data and cache
        setAiInsightsData(result.data);
        setAiInsightsCache({
          data: result.data,
          cacheKey: cacheKey,
          timestamp: now,
        });
        setIsDrawerOpen(true); // Only open drawer on success
      } else {
        // Show error notification toast
        dispatch(
          commonSlice.actions.handleToast({
            isOpen: true,
            content: result.msg || 'Failed to fetch AI insights',
            error: true,
          }),
        );
      }
    } catch (err: any) {
      console.error('Error fetching AI insights:', err);
      // Show error notification toast
      dispatch(
        commonSlice.actions.handleToast({
          isOpen: true,
          content: err?.data?.msg || err?.message || 'An unexpected error occurred while fetching insights',
          error: true,
        }),
      );
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  // Close drawer when date range or tab changes
  useEffect(() => {
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
      setAiInsightsData(null);
    }
    // Note: Cache is preserved when date range or tab changes
    // User can click button to get cached insights if within 1 hour
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleDatePicker, tabSelected]);

  // Check if we should open Trakow Assistant (redirected from Dashboard)
  useEffect(() => {
    if (shouldOpenTrakowAssistant) {
      // Reset the flag
      dispatch(commonSlice.actions.setShouldOpenTrakowAssistant(false));
      // Trigger the API call to open drawer
      handleOpenDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpenTrakowAssistant]);

  const tabsConfig: Array<ITabConfig> = [
    {
      id: ShipmentAnalyticsTabs.ShipmentsOverview,
      content: (
        <Text as="span" variant="bodyLg" fontWeight="semibold">
          Shipments overview
        </Text>
      ),
      panelID: ShipmentAnalyticsTabs.ShipmentsOverview,
      component: <ShipmentsOverview start={start} end={end} title={titleDatePicker} />,
    },
    {
      id: ShipmentAnalyticsTabs.FulfillmentPerformance,
      content: (
        <Text as="span" variant="bodyLg" fontWeight="semibold">
          Fulfillment performance&nbsp;&nbsp;
          {isLockFeature([AccountPlan.Starter, AccountPlan.Basic]) && (
            <TabBadge tone="info" locked>
              Scale
            </TabBadge>
          )}
        </Text>
      ),
      panelID: ShipmentAnalyticsTabs.FulfillmentPerformance,
      component: <FulfillmentPerformance start={start} end={end} title={titleDatePicker} />,
    },
  ];

  return (
    <StyledShipmentAnalytics>
      <Page
        title="Analytics"
        subtitle="From order to delivery, designed to support your business decisions"
        compactTitle
        fullWidth
        titleMetadata={
          <InlineStack blockAlign="end" align="end" gap="300">
            <TrakowAssistantButton
              onClick={handleOpenDrawer}
              loading={isLoadingInsights}
              allowForStarter={tabSelected === ShipmentAnalyticsTabs.ShipmentsOverview}
            />
            <InlineStack blockAlign="center" gap="100">
              <DatePicker
                start={start}
                end={end}
                titleBtn={titleDatePicker}
                setTitleBtn={handleChangeTitleBtn}
                onSave={handleChangeDatePicker}
                disabled={isViewDemo}
              />
              <Tooltip
                content="Analytics date range filters by order created date."
                dismissOnMouseOut
              >
                <Icon source={InfoIcon} tone="subdued" />
              </Tooltip>
            </InlineStack>
          </InlineStack>
        }
      >
        <LegacyTabs tabs={tabsConfig} selected={tabIndex} onSelect={handleChangeTab} />
        <div style={{ marginTop: '1rem' }}>{tabsConfig[tabIndex]?.component}</div>
      </Page>
      <TrakowAssistantDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        data={aiInsightsData || undefined}
        loading={isLoadingInsights}
      />
    </StyledShipmentAnalytics>
  );
};

export default ShipmentAnalytics;
